import { format, parseISO, isValid } from 'date-fns';
import clsx from 'clsx';

// Date formatting utilities
export const formatDate = (date, formatString = 'MMM dd, yyyy') => {
  if (!date) return '';
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  return isValid(parsedDate) ? format(parsedDate, formatString) : '';
};

export const formatDateTime = (date) => formatDate(date, 'MMM dd, yyyy HH:mm');
export const formatTime = (date) => formatDate(date, 'HH:mm');

// Status formatting
export const getStatusColor = (status) => {
  const statusColors = {
    // Service call statuses
    scheduled: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    rescheduled: 'bg-orange-100 text-orange-800',
    return: 'bg-yellow-100 text-yellow-800',
    
    // Generator statuses
    operational: 'bg-green-100 text-green-800',
    maintenance_required: 'bg-yellow-100 text-yellow-800',
    out_of_service: 'bg-red-100 text-red-800',
    retired: 'bg-gray-100 text-gray-800',
    
    // Priority levels
    low: 'bg-gray-100 text-gray-800',
    medium: 'bg-blue-100 text-blue-800',
    high: 'bg-orange-100 text-orange-800',
    urgent: 'bg-red-100 text-red-800',
    
    // User status
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-gray-100 text-gray-800',
  };
  
  return statusColors[status] || 'bg-gray-100 text-gray-800';
};

export const getStatusText = (status) => {
  const statusTexts = {
    // Service call statuses
    scheduled: 'Scheduled',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
    rescheduled: 'Rescheduled',
    return: 'Return',
    
    // Generator statuses
    operational: 'Operational',
    maintenance_required: 'Maintenance Required',
    out_of_service: 'Out of Service',
    retired: 'Retired',
    
    // Priority levels
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    urgent: 'Urgent',
    
    // User status
    active: 'Active',
    inactive: 'Inactive',
  };
  
  return statusTexts[status] || status;
};

export const getResolutionDisplay = (serviceCall) => {
  if (serviceCall?.resolution === 'skipped') {
    return { label: 'Skipped', color: 'bg-amber-100 text-amber-800' };
  }
  if (serviceCall?.resolution === 'bumped') {
    return { label: 'Bumped', color: 'bg-orange-100 text-orange-800' };
  }
  return null;
};

// Form validation utilities
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone) => {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
};

// String utilities
export const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const truncate = (str, length = 50) => {
  if (!str) return '';
  return str.length > length ? str.substring(0, length) + '...' : str;
};

// Number formatting
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

export const formatNumber = (number) => {
  return new Intl.NumberFormat('en-US').format(number);
};

// Client name display utility
export const getClientDisplayName = (client) => {
  if (!client) return 'Unknown Customer';
  if (client.isCommercial && client.companyName) return client.companyName;
  const fullName = [client.contactFirstName, client.contactLastName].filter(Boolean).join(' ').trim();
  return fullName || client.companyName || 'Unknown Customer';
};

/**
 * Display name for service call list/PDF rows: C Spire (site + WO), Franklin (site),
 * commercial (company name), residential ("Last, First").
 * @param {object} serviceCall
 */
export const getServiceCallListCustomerName = (serviceCall) => {
  const client = serviceCall?.client;

  if (serviceCall?.isCspire) {
    const wo = serviceCall.cspireWorkOrder;
    const site = wo?.site;
    const cspireLocation =
      wo?.locationName ||
      site?.locationName ||
      site?.siteId ||
      '';
    const workOrderNumber = wo?.workOrderNumber || '';
    const locationLabel = cspireLocation ? ` - ${cspireLocation}` : '';
    const workOrderLabel = workOrderNumber ? ` (${workOrderNumber})` : '';
    return `CSpire${locationLabel}${workOrderLabel}`;
  }

  if (serviceCall?.isFranklin) {
    const fs = serviceCall.franklinSite;
    const loc = fs?.locationName || fs?.siteId || 'Site';
    return `Franklin - ${loc}`;
  }

  if (client?.isCommercial && client?.companyName) {
    return client.companyName;
  }

  if (client?.contactLastName && client?.contactFirstName) {
    return `${client.contactLastName}, ${client.contactFirstName}`;
  }

  return (
    client?.contactLastName ||
    client?.contactFirstName ||
    client?.companyName ||
    'N/A'
  );
};

/**
 * City / state for list/PDF: prefer C Spire site or Franklin site when applicable.
 * @param {object} serviceCall
 */
export const getServiceCallListCityState = (serviceCall) => {
  const client = serviceCall?.client;

  if (serviceCall?.isCspire) {
    const site = serviceCall.cspireWorkOrder?.site;
    const city = site?.city || client?.city;
    const state = site?.state || client?.state;
    return [city, state].filter(Boolean).join(', ');
  }

  if (serviceCall?.isFranklin && serviceCall?.franklinSite) {
    const fs = serviceCall.franklinSite;
    const city = fs.city || client?.city;
    const state = fs.state || client?.state;
    return [city, state].filter(Boolean).join(', ');
  }

  return [client?.city, client?.state].filter(Boolean).join(', ');
};

// Class name utilities
export const cn = (...inputs) => clsx(inputs);

// Debounce utility
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Local storage utilities
export const storage = {
  get: (key) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Handle storage errors silently
    }
  },
  remove: (key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Handle storage errors silently
    }
  },
};

// Error handling utilities
export const getErrorMessage = (error) => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

// Pagination utilities
export const getPaginationInfo = (pagination) => {
  if (!pagination) return null;
  
  const { page, limit, total, pages } = pagination;
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  
  return {
    start,
    end,
    total,
    page,
    pages,
    hasNext: page < pages,
    hasPrev: page > 1,
  };
};
